import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Typography, Grid, Button, TextField, InputAdornment , IconButton, Stack, ThemeProvider, FormControlLabel, Checkbox, Divider } from '@mui/material';
import config from 'src/config';
import Iconify from 'src/components/iconify';
import { useNavigate } from 'react-router-dom';
import { getToken, register } from 'src/services/auth.service';
import { createTheme } from '@mui/material/styles'
import { AuthHeader } from 'src/components/auth/header';
import { AuthFooter } from 'src/components/auth/footer';
import SSOLogin from 'src/components/auth/sso';

const theme = createTheme({
    palette: {
      primary: {
        main: '#ff9933'
      }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    textTransform: 'none'
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderRadius: '8px',
                        },
                    },
                },
            },
        },
    }
});

class IndividualRegisterPage extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            show_password: false,
            show_confirm_password: false,

            pristine: {
                company_name: true,
                name: true,
                username: true,
                password: true,
                confirm_password: true
            },

            agree_to_terms: false,
            registering: false,

            // payload
            account_type: 'personal',
            company_name: '',
            name: '',
            username: '',
            password: '',
            confirm_password: ''
        }

    }

    validateName(name, checkPristine = true) {

        if (checkPristine && this.state.pristine.name) {
            return true;
        }

        if (!name.trim()) {
            return false;
        }

        return true;
    }

    validateUsername(username, checkPristine = true) {

        if (checkPristine && this.state.pristine.username) {
            return true;
        }

        if (!username) {
            return false;
        }

        if (username.indexOf('@') === -1) {
            return false;
        }

        if (username.indexOf('.') === -1) {
            return false;
        }

        return true;
    }

    validatePassword(password, checkPristine = true) {

        if (checkPristine && this.state.pristine.password) {
            return true;
        }

        if (!password) {
            return false;
        }

        if (password.length < 12) {
            return false;
        }

        return true;
    }

    validateConfirmPassword(confirm_password, checkPristine = true) {
        if (checkPristine && this.state.pristine.confirm_password) {
            return true;
        }

        if (!confirm_password) {
            return false;
        }

        if (confirm_password !== this.state.password) {
            return false;
        }

        return true;
    }

    validateForm() {
        return this.validateName(this.state.name, false) && this.validateUsername(this.state.username, false) && this.validatePassword(this.state.password, false) && this.validateConfirmPassword(this.state.confirm_password, false) && this.state.agree_to_terms;
    }

    async handleRegister() {

        this.setState({ registering: true });

        let payload = {
            account_type: this.state.account_type,
            name: this.state.name,
            username: this.state.username,
            password: this.state.password
        };

        let registerRes = await register(payload);
        if (registerRes.error) {
            console.log(registerRes.error);
            this.setState({ registering: false });
            alert('Error: ' + registerRes.error);
            return;
        }

        this.setState({ registering: false });

        if (registerRes.token) {
            this.props.navigate('/registersuccess?type=personal');
        }
    }

    render() {

        return (
            <ThemeProvider theme={theme}>
                <Helmet>
                    <title> Individual User Register | {config.APP_NAME} </title>
                </Helmet>

                <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', p: 2 }}>
                    <Grid item xs={12} sm={8} md={6} lg={3} xl={3}>


                        <AuthHeader
                            text={'Create your individual account'}
                        />

                        <Typography sx={{ fontWeight: 'bold', mt: 5 }}>
                            Full Name
                        </Typography>
                        <TextField
                            fullWidth
                            name="name"
                            size='small'
                            sx={{ mb: 2 }}
                            value={this.state.name}
                            onChange={(e) => {
                                this.setState({
                                    name: e.target.value,
                                    pristine: {
                                        ...this.state.pristine,
                                        name: false
                                    }
                                });
                            }}
                            placeholder='John Doe'
                        />


                        <Typography sx={{ fontWeight: 'bold' }}>
                            Email address
                        </Typography>
                        <TextField
                            fullWidth
                            name="email"
                            size='small'
                            sx={{ mb: 2 }}
                            value={this.state.username}
                            onChange={(e) => {
                                this.setState({
                                    username: e.target.value,
                                    pristine: {
                                        ...this.state.pristine,
                                        username: false
                                    }
                                });
                            }}
                            placeholder='you@example.com'
                            error={!this.validateUsername(this.state.username)}
                            helperText={!this.validateUsername(this.state.username) ? 'Invalid email address' : ''}
                        />

                        <Typography sx={{ fontWeight: 'bold' }}>
                            Password
                        </Typography>
                        <TextField
                            fullWidth
                            name="password"
                            size='small'
                            placeholder='••••••••'
                            type={this.state.show_password ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                this.setState({ show_password: !this.state.show_password });
                                            }}
                                            edge="end"
                                        >
                                            <Iconify icon={this.state.show_password ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            value={this.state.password}
                            onChange={(e) => {
                                this.setState({
                                    password: e.target.value,
                                    pristine: {
                                        ...this.state.pristine,
                                        password: false
                                    }
                                });
                            }}
                            error={!this.validatePassword(this.state.password)}
                            helperText={!this.validatePassword(this.state.password) ? 'Password must be at least 12 characters' : 'Use at least 12 characters with a mix of uppercase, lowercase and numbers'}
                            FormHelperTextProps={{ sx: { m: 0 } }}
                        />

                        <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                            Confirm Password
                        </Typography>
                        <TextField
                            fullWidth
                            name="confirm_password"
                            size='small'
                            placeholder='••••••••'
                            type={this.state.show_confirm_password ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                this.setState({ show_confirm_password: !this.state.show_confirm_password });
                                            }}
                                            edge="end"
                                        >
                                            <Iconify icon={this.state.show_confirm_password ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            value={this.state.confirm_password}
                            onChange={(e) => {
                                this.setState({
                                    confirm_password: e.target.value,
                                    pristine: {
                                        ...this.state.pristine,
                                        confirm_password: false
                                    }
                                });
                            }}
                            error={!this.validateConfirmPassword(this.state.confirm_password)}
                            helperText={!this.validateConfirmPassword(this.state.confirm_password) ? 'Password must match' : ''}
                        />

                        <Stack direction="row" sx={{ mb: 2 }} justifyContent={'space-between'}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.agree_to_terms}
                                        onChange={(e) => {
                                            this.setState({ agree_to_terms: e.target.checked });
                                        }}
                                        sx={{ color: 'gray' }}
                                    />
                                }
                                label={
                                    <Typography style={{ fontSize: '0.875rem', color: 'gray' }}>
                                        I agree to the {' '}
                                        <Link href="https://dmonk.ai/terms.html" underline="hover" target="_blank" rel="noopener noreferrer">
                                            Terms of Use
                                        </Link>
                                        {' '} and {' '}
                                        <Link href="https://dmonk.ai/privacyusers.html" underline="hover" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Stack>

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            onClick={this.handleRegister.bind(this)}
                            disabled={!this.validateForm() || this.state.registering}
                            style={{ color: 'white' }}
                        >
                            {this.state.registering ? 'Signing Up...' : 'Signup'}
                        </Button>

                        {/* Already have an account */}
                        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ color: 'gray', mr: 1 }}>
                                Already have an account? {' '}
                                <Link
                                    underline="hover"
                                    onClick={() => {
                                        this.props.navigate('/login');
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Log In
                                </Link>
                            </Typography>
                        </Stack>

                        {/* Horizontal line with text inbetween */}
                        <Divider sx={{ my: 2 }}>
                            <Typography sx={{ color: 'gray' }}>
                                or
                            </Typography>
                        </Divider>

                        <SSOLogin />

                        <AuthFooter />

                    </Grid>
                </Grid>
            </ThemeProvider>
        )
    }
}

export default function (props) {
    const navigate = useNavigate();

    return <IndividualRegisterPage {...props} navigate={navigate} />;
}

